import { OverQuotaNotice } from "@components/OverQuotaNotice";
import { createNucleotideSequence } from "@db/createNucleotideSequence";
import { parseSequenceInput } from "@formats/sequence-input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCurrentUser } from "@hooks/use-current-user";
import { useStorageQuota } from "@hooks/use-storage-quota";
import { formatBp } from "@lib/genetics";
import { Button } from "@ui/button";
import { DialogFooter } from "@ui/dialog";
import { Field, FieldContent, FieldError, FieldLabel } from "@ui/field";
import { Input } from "@ui/input";
import { Textarea } from "@ui/textarea";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const simpleFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  sequence: z.string().min(1, "Sequence is required"),
});

type SimpleFormData = z.infer<typeof simpleFormSchema>;

type SimpleFormProps = {
  onClose: () => void;
};

export function SimpleForm({ onClose }: SimpleFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SimpleFormData>({
    resolver: zodResolver(simpleFormSchema),
    defaultValues: { name: "", sequence: "" },
  });

  const { user } = useCurrentUser();
  const { isOverQuota } = useStorageQuota();
  const ownerId = user?.id ?? "local";

  const name = watch("name");
  const sequence = watch("sequence");

  const parseResult = useMemo(
    () => parseSequenceInput(sequence, name),
    [sequence, name],
  );

  const hasErrors = parseResult.errors.length > 0;
  const hasSequences = parseResult.sequences.length > 0;
  const canSubmit =
    hasSequences && !hasErrors && name.trim() !== "" && !isOverQuota;

  async function onSubmit(data: SimpleFormData) {
    const result = parseSequenceInput(data.sequence, data.name);
    if (result.errors.length > 0 || result.sequences.length === 0) return;

    const seq = result.sequences[0];

    await createNucleotideSequence({
      name: seq.name,
      sequence: seq.sequence,
      ownerId,
      topology: "linear",
    });

    onClose();
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex h-80 flex-col gap-4 py-4">
        <Field>
          <FieldLabel htmlFor="simple-name">Name</FieldLabel>
          <FieldContent>
            <Input id="simple-name" {...register("name")} />
          </FieldContent>
          <FieldError errors={[errors.name]} />
        </Field>

        <Field className="flex-1">
          <FieldLabel htmlFor="simple-sequence">Sequence</FieldLabel>
          <FieldContent className="flex-1">
            <Textarea
              id="simple-sequence"
              {...register("sequence")}
              className="h-full resize-none break-all font-mono"
            />
          </FieldContent>
          <FieldError errors={[errors.sequence]} />
        </Field>

        <div className="text-muted-foreground text-sm">
          {isOverQuota ? (
            <OverQuotaNotice />
          ) : hasSequences ? (
            formatBp(parseResult.sequences[0].sequence.length)
          ) : (
            "\u00A0"
          )}
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          Create
        </Button>
      </DialogFooter>
    </form>
  );
}
